import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { getInsurancePolicyIntegrationPrefilledFields } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.integration';
import {
  defaultPolicy,
  Fields,
  INSURANCE_POLICIES_DETAILS_SUBFORM,
  InsurancePolicyDetails,
} from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { InsurancePolicyForm } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.UI';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';

import {
  LifeInsuranceStepFormShape,
  LifeInsuranceStepRowData,
} from './LifeInsuranceStep.types';

/**
 * Maps a LifeInsuranceStepRowData to an InsurancePolicyDetails for the local form.
 * Fields not present in the step row data get defaults from defaultPolicy.
 */
function toInsurancePolicyDetails(
  row: LifeInsuranceStepRowData
): InsurancePolicyDetails {
  return {
    ...defaultPolicy,
    integrationInsuranceId: row.integrationInsuranceId,
    policyType: row.policyType,
    isConvertible: row.isConvertible,
    conversionDate: row.conversionDate,
    insuranceCarrier: row.insuranceCarrier,
    policyNumber: row.policyNumber,
    policyHolderIDs: row.policyHolderIDs,
    initialPolicyHolderIDs: row.policyHolderIDs,
    deathBenefitAmount: row.deathBenefitAmount,
    policyStartDate: row.policyStartDate,
    termLength: row.termLength,
    premiumAmount: row.premiumAmount,
    premiumFrequency: row.premiumFrequency,
    premiumInitialDueDate: row.premiumInitialDueDate,
    cashValue: row.cashValue,
    cashValueDate: row.cashValueDate ?? new Date(),
    loanBalanceOutstanding: row.loanBalanceOutstanding,
    notes: row.notes,
  };
}

/**
 * Extracts the editable fields from an InsurancePolicyDetails back into
 * the shape expected by LifeInsuranceStepRowData, preserving step-only
 * fields from the current row.
 */
function fromInsurancePolicyDetails(
  policy: InsurancePolicyDetails,
  currentRow: LifeInsuranceStepRowData
): LifeInsuranceStepRowData {
  return {
    ...currentRow,
    integrationInsuranceId:
      policy.integrationInsuranceId || currentRow.integrationInsuranceId,
    policyType: policy.policyType,
    isConvertible: policy.isConvertible,
    conversionDate: policy.conversionDate,
    insuranceCarrier: policy.insuranceCarrier,
    policyNumber: policy.policyNumber,
    policyHolderIDs: policy.policyHolderIDs,
    deathBenefitAmount: policy.deathBenefitAmount,
    policyStartDate: policy.policyStartDate,
    termLength: policy.termLength,
    premiumAmount: policy.premiumAmount,
    premiumFrequency: policy.premiumFrequency,
    premiumInitialDueDate: policy.premiumInitialDueDate,
    cashValue: policy.cashValue,
    cashValueDate: policy.cashValueDate,
    loanBalanceOutstanding: policy.loanBalanceOutstanding,
    notes: policy.notes,
  };
}

// no-op remove function — the linking drawer renders a single policy, not a repeater
const noop = () => {};

interface LifeInsuranceStepEditDrawerProps {
  policyId: string;
  onClose: () => void;
}

export function LifeInsuranceStepEditDrawer({
  policyId,
  onClose,
}: LifeInsuranceStepEditDrawerProps) {
  const { setValue, getValues } = useFormContext<LifeInsuranceStepFormShape>();
  const { householdId } = useHouseholdDetailsContext();

  const rowData = getValues(`policies.${policyId}`);
  const initialPrefilledFields = useMemo(
    () => getInsurancePolicyIntegrationPrefilledFields(rowData),
    [rowData]
  );

  // Create a local form matching the InsurancePolicyDetailsSubform shape
  // with a single policy at index 0. This lets us reuse InsurancePolicyForm
  // directly without any extraction.
  const localFormMethods = useForm<Fields>({
    defaultValues: useMemo(
      () => ({
        [INSURANCE_POLICIES_DETAILS_SUBFORM]: {
          policies: [toInsurancePolicyDetails(rowData)],
          initialPolicyIDs: [],
        },
      }),
      [rowData]
    ),
  });

  const handleSave = useCallback(() => {
    const policies = localFormMethods.getValues(
      `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies`
    );
    // The local form always has exactly one policy at index 0
    const policy = policies[0];
    if (!policy) return;

    const currentRow = getValues(`policies.${policyId}`);
    setValue(
      `policies.${policyId}`,
      fromInsurancePolicyDetails(policy, currentRow)
    );
    onClose();
  }, [localFormMethods, getValues, setValue, policyId, onClose]);

  return (
    <SideDrawer
      isOpen
      onClose={onClose}
      width={OVERLAY_MD}
      heading="Edit insurance policy"
      body={
        <FormProvider {...localFormMethods}>
          <Stack pt={2}>
            <InsurancePolicyForm
              policyIndex={0}
              // householdId is always set in the linking flow since we're
              // operating within a household context
              householdId={householdId!}
              variant="default"
              internalCardVariantOverride="filled-outline"
              remove={noop}
              additionalItemRendererProps={{ hideDeleteButton: true }}
              hideDocuments
              hidePremiumPaymentSource
              isIntegrationLinked
              initialIntegrationPrefilledFields={initialPrefilledFields}
              hideIntegrationSelect
            />
          </Stack>
        </FormProvider>
      }
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Save changes
          </Button>
        </Stack>
      }
    />
  );
}
